import { useState, useCallback, useEffect } from 'react';
import { FORM30_TABLES } from '../constants/form30Tables';
import { useFetchForm30 } from './useQuery/useFetchForm30';
import { useSaveForm30 } from './useQuery/useSaveForm30';

/**
 * Хук для управления данными формы 30
 */
export const useForm30Data = (sectorId: string | null, year: number) => {
    // Функция для создания пустых данных
    const createEmptyTableData = useCallback(() => {
        const initialData: Record<string, Record<string, Record<string, number | null>>> = {};
        FORM30_TABLES.forEach(table => {
            initialData[table.id] = {};
            table.rows.forEach(row => {
                initialData[table.id][row.id] = {};
                table.columns.forEach(col => {
                    if (col.type === 'number') {
                        initialData[table.id][row.id][col.key] = null;
                    }
                });
            });
        });
        return initialData;
    }, []);

    // Состояние данных таблицы
    const [tableData, setTableData] = useState<Record<string, Record<string, Record<string, number | null>>>>(
        createEmptyTableData
    );

    // Получаем данные с сервера
    const { form30Data, form30IsLoading, form30Error } = useFetchForm30(sectorId, year, Boolean(sectorId));

    // Мутация для сохранения
    const { mutate: saveForm30Mutation, isPending: isSaving } = useSaveForm30();

    // Загружаем данные при изменении года или сектора
    useEffect(() => {
        if (form30Data) {
            setTableData(form30Data.data);
        } else if (!form30IsLoading) {
            setTableData(createEmptyTableData());
        }
    }, [form30Data, form30IsLoading, createEmptyTableData]);

    // Обработчик изменения данных
    const handleTableDataChange = useCallback((
        tableId: string,
        rowId: string,
        columnKey: string,
        value: number | null
    ) => {
        setTableData(prev => {
            const newData = { ...prev };
            if (!newData[tableId]) {
                newData[tableId] = {};
            }
            if (!newData[tableId][rowId]) {
                newData[tableId][rowId] = {};
            }
            newData[tableId][rowId][columnKey] = value;
            return newData;
        });
    }, []);

    // Обработчик импорта данных
    const handleImportData = useCallback((
        importedData: Record<string, Record<string, Record<string, number | null>>>
    ) => {
        setTableData(importedData);
    }, []);

    // Обработчик импорта и сохранения
    const handleImportAndSave = useCallback(async (
        importedData: Record<string, Record<string, Record<string, number | null>>>
    ) => {
        if (!sectorId) {
            throw new Error('Не выбран регион');
        }

        setTableData(importedData);

        return new Promise<void>((resolve, reject) => {
            saveForm30Mutation(
                { sector_id: sectorId, year, data: importedData },
                {
                    onSuccess: () => resolve(),
                    onError: (error) => reject(error)
                }
            );
        });
    }, [sectorId, year, saveForm30Mutation]);

    // Обработчик сохранения
    const handleSave = useCallback(() => {
        if (!sectorId) {
            throw new Error('Не выбран регион');
        }

        saveForm30Mutation({ sector_id: sectorId, year, data: tableData });
    }, [sectorId, year, tableData, saveForm30Mutation]);

    return {
        tableData,
        isLoading: form30IsLoading,
        isSaving,
        error: form30Error,
        handleTableDataChange,
        handleImportData,
        handleImportAndSave,
        handleSave,
    };
};
